import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Download, ArrowUpRight, Loader2, Sparkles, ChevronRight, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const triggerDownload = (filename) => {
    const a = document.createElement("a");
    a.href = `/resources/${filename}`;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

const logError = (...args) => {
    if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error(...args);
    }
};

const buildPayload = (form, lang, selected) => ({
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim().toLowerCase(),
    nationality: form.nationality.trim(),
    language: lang || "it",
    resource: selected.id,
    source: typeof window !== "undefined" ? window.location.hostname : "",
});

const isFormValid = (form) =>
    !!form.firstName.trim() && !!form.lastName.trim() && EMAIL_RE.test(form.email);

const useResourceSubmit = ({ t, lang, selected, onSuccess }) => {
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const submit = async (form) => {
        if (!isFormValid(form)) {
            setStatus("error");
            setErrorMsg(t.resources.errorValidation);
            return;
        }
        setStatus("loading");
        setErrorMsg("");

        try {
            const resp = await fetch(`${API}/resources/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(buildPayload(form, lang, selected)),
            });
            if (!resp.ok) {
                const errBody = await resp.json().catch(() => ({}));
                throw new Error(errBody.detail || `HTTP ${resp.status}`);
            }
            triggerDownload(selected.file);
            onSuccess(form.firstName.trim());
        } catch (err) {
            logError("[Resources] submit error", err);
            setStatus("error");
            setErrorMsg(t.resources.errorGeneric);
        }
    };

    return { status, errorMsg, submit };
};

const FuelingHubCard = ({ hub }) => (
    <a
        href={hub.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="fueling-hub-card"
        className="group relative block mb-6 md:mb-8 overflow-hidden rounded-sm border border-fw/40 bg-gradient-to-br from-fw/[0.08] via-black to-black hover:border-fw hover:shadow-[0_0_40px_-8px_rgba(57,255,20,0.45)] transition-all duration-500"
    >
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-fw" aria-hidden="true" />
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-fw/15 blur-[100px] pointer-events-none" />

        <div className="relative z-10 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-fw/60 rounded-sm font-mono text-[9px] uppercase tracking-[0.22em] text-fw">
                        <Zap className="w-2.5 h-2.5" strokeWidth={2.5} />
                        {hub.badge}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw/70">
                        {hub.overline}
                    </span>
                </div>
                <h3 className="font-heavy text-xl md:text-2xl lg:text-3xl uppercase leading-tight text-white text-balance">
                    {hub.title}
                </h3>
                <p className="mt-3 text-sm md:text-base text-white/75 leading-relaxed max-w-xl">
                    {hub.description}
                </p>
            </div>

            <span
                data-testid="fueling-hub-cta"
                className="flex-none inline-flex items-center justify-center gap-2 px-6 py-4 bg-fw text-black font-display font-semibold text-xs uppercase tracking-[0.22em] rounded-sm group-hover:bg-fw-glow group-hover:glow-green transition-all duration-300 whitespace-nowrap self-start md:self-auto"
            >
                {hub.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2.5} />
            </span>
        </div>
    </a>
);

const ResourceCard = ({ item, isSelected, onSelect }) => (
    <button
        type="button"
        onClick={onSelect}
        data-testid={`resource-card-${item.id}`}
        className={`group relative w-full h-full text-left p-4 border rounded-sm transition-all duration-300 flex flex-col ${
            isSelected
                ? "border-fw bg-fw/10"
                : "border-white/12 bg-white/[0.02] hover:border-fw/40 hover:bg-fw/[0.04]"
        }`}
    >
        <div className="flex items-start justify-between gap-2 mb-2">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-sm font-mono text-[9px] uppercase tracking-[0.18em] ${
                isSelected ? "border-fw/60 text-fw" : "border-white/15 text-white/55"
            }`}>
                <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                {item.badge}
            </span>
            <span className={`flex-none w-4 h-4 rounded-full border flex items-center justify-center ${
                isSelected ? "border-fw bg-fw/30" : "border-white/30"
            }`}>
                {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-fw" />}
            </span>
        </div>
        <h3 className={`font-display text-base font-semibold leading-tight mb-1 ${
            isSelected ? "text-white" : "text-white/90"
        }`}>
            {item.title}
        </h3>
        <p className={`text-[12px] leading-snug ${
            isSelected ? "text-fw/90" : "text-white/55"
        }`}>
            {item.subtitle}
        </p>
    </button>
);

const FormField = ({ label, type = "text", value, onChange, placeholder, testId, autoComplete, required }) => (
    <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">{label}</span>
        <input
            type={type}
            required={required}
            value={value}
            onChange={onChange}
            data-testid={testId}
            autoComplete={autoComplete}
            className="mt-2 w-full bg-transparent border-b border-white/25 focus:border-fw outline-none py-2 text-white placeholder-white/30 transition-colors"
            placeholder={placeholder}
        />
    </label>
);

const SubmitButton = ({ loading, label, loadingLabel }) => (
    <button
        type="submit"
        disabled={loading}
        data-testid="resources-submit-button"
        className="group relative w-full sm:w-auto flex items-center justify-center gap-3 px-7 py-4 bg-fw text-black font-display font-semibold text-sm uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
    >
        {loading ? (
            <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                {loadingLabel}
            </>
        ) : (
            <>
                <Download className="w-4 h-4" strokeWidth={2} />
                {label}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} />
            </>
        )}
    </button>
);

const ResourcesForm = ({ t, lang, selected, onSuccess }) => {
    const [form, setForm] = useState({ firstName: "", lastName: "", email: "", nationality: "" });
    const { status, errorMsg, submit } = useResourceSubmit({ t, lang, selected, onSuccess });
    const handleChange = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        submit(form);
    };

    return (
        <form onSubmit={handleSubmit} data-testid="resources-form" className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                    label={t.resources.firstNameLabel}
                    placeholder={t.resources.firstNamePlaceholder}
                    value={form.firstName}
                    onChange={handleChange("firstName")}
                    testId="resources-input-firstname"
                    autoComplete="given-name"
                    required
                />
                <FormField
                    label={t.resources.lastNameLabel}
                    placeholder={t.resources.lastNamePlaceholder}
                    value={form.lastName}
                    onChange={handleChange("lastName")}
                    testId="resources-input-lastname"
                    autoComplete="family-name"
                    required
                />
            </div>
            <FormField
                label={t.resources.emailLabel}
                placeholder={t.resources.emailPlaceholder}
                value={form.email}
                onChange={handleChange("email")}
                testId="resources-input-email"
                autoComplete="email"
                type="email"
                required
            />
            <FormField
                label={t.resources.nationalityLabel}
                placeholder={t.resources.nationalityPlaceholder}
                value={form.nationality}
                onChange={handleChange("nationality")}
                testId="resources-input-nationality"
                autoComplete="country-name"
            />

            <p className="text-[11px] leading-relaxed text-white/45 pt-1">{t.resources.privacyNote}</p>

            {status === "error" && (
                <p data-testid="resources-error" className="text-sm text-red-400">
                    {errorMsg || t.resources.errorGeneric}
                </p>
            )}

            <SubmitButton
                loading={status === "loading"}
                label={t.resources.submitLabel}
                loadingLabel={t.resources.submitting}
            />
        </form>
    );
};

const ResourcesSuccess = ({ t, name, selected, onDownloadAgain }) => (
    <div data-testid="resources-success" className="border border-fw/40 bg-fw/[0.04] rounded-sm p-7">
        <div className="flex items-start gap-4">
            <span className="flex-none w-10 h-10 rounded-full bg-fw/15 border border-fw/40 flex items-center justify-center text-fw">
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2} />
            </span>
            <div className="flex-1">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw mb-2">
                    {t.resources.successOverline}
                </p>
                <h3 className="font-heavy text-2xl md:text-3xl uppercase text-white leading-tight">
                    {t.resources.successTitle(name)}
                </h3>
                <p className="mt-3 text-sm md:text-base text-white/70 leading-relaxed">
                    {t.resources.successText}
                </p>
                <div className="mt-4 px-4 py-3 border border-white/10 bg-black/40 rounded-sm">
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/45">
                        {t.resources.selectedLabel}
                    </p>
                    <p className="font-display text-base font-semibold text-white mt-1">
                        {selected.title}
                    </p>
                </div>
                <button
                    onClick={() => onDownloadAgain(selected.file)}
                    data-testid="resources-download-again"
                    className="mt-5 inline-flex items-center gap-2 text-fw hover:underline font-display text-sm uppercase tracking-[0.16em]"
                >
                    <Download className="w-4 h-4" strokeWidth={2} />
                    {t.resources.downloadAgain}
                </button>
            </div>
        </div>
    </div>
);

export const Resources = ({ id = "risorse" }) => {
    const { t, lang } = useLanguage();
    const navigate = useNavigate();
    const items = t.resources.items;
    const [selectedId, setSelectedId] = useState(items[0].id);
    const [submitted, setSubmitted] = useState(false);
    const [submittedName, setSubmittedName] = useState("");

    const selected = items.find((it) => it.id === selectedId) || items[0];

    const handleSuccess = (name) => {
        setSubmittedName(name);
        setSubmitted(true);
        // Brief loading state, then redirect to dedicated conversion page.
        // 700ms is enough for the browser to start the PDF download in another tab.
        setTimeout(() => {
            const qs = new URLSearchParams({ source: "resource", name, guida: selected.id });
            navigate(`/grazie?${qs.toString()}`);
        }, 700);
    };

    return (
        <section
            id={id}
            data-testid={`resources-section-${id}`}
            className="relative bg-black text-white py-12 md:py-16 lg:py-20 overflow-hidden"
        >
            <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-fw/10 blur-[150px] pointer-events-none" />

            <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10 lg:px-16">
                <div className="grid grid-cols-12 gap-6 lg:gap-10">
                    <div className="col-span-12 lg:col-span-7">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-10 h-px bg-fw" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw">
                                {t.resources.overline}
                            </span>
                        </div>
                        <h2
                            data-testid="resources-headline"
                            className="font-heavy text-3xl md:text-4xl lg:text-5xl leading-[0.98] uppercase text-balance"
                        >
                            {t.resources.headline}
                        </h2>
                        <p
                            data-testid="resources-description"
                            className="mt-3 text-sm md:text-base text-white/70 leading-relaxed max-w-2xl mb-6 md:mb-8"
                        >
                            {t.resources.description}
                        </p>

                        <FuelingHubCard hub={t.resources.fuelingHub} />

                        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55 mb-3">
                            {t.resources.chooseLabel}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {items.map((item) => (
                                <ResourceCard
                                    key={item.id}
                                    item={item}
                                    isSelected={selectedId === item.id}
                                    onSelect={() => setSelectedId(item.id)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-5">
                        <div className="border border-white/12 bg-white/[0.02] backdrop-blur-sm rounded-sm p-7 md:p-9 lg:sticky lg:top-28">
                            {submitted ? (
                                <ResourcesSuccess
                                    t={t}
                                    name={submittedName}
                                    selected={selected}
                                    onDownloadAgain={triggerDownload}
                                />
                            ) : (
                                <>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw mb-2">
                                        {t.resources.formOverline}
                                    </p>
                                    <h3 className="font-display text-xl md:text-2xl font-semibold text-white leading-tight mb-2">
                                        {t.resources.formTitle}
                                    </h3>
                                    <div className="flex items-center gap-2 mb-6 pb-5 border-b border-white/10">
                                        <ChevronRight className="w-4 h-4 text-fw" strokeWidth={2} />
                                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">
                                            {t.resources.selectedLabel}:
                                        </p>
                                        <p className="font-display text-sm font-semibold text-fw">
                                            {selected.title}
                                        </p>
                                    </div>
                                    <ResourcesForm
                                        t={t}
                                        lang={lang}
                                        selected={selected}
                                        onSuccess={handleSuccess}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
